import React from "react";
import PropTypes from "prop-types";
import { useMediaQuery } from "@mui/material";

const Pagination = ({
  totalItem = 0,
  itemPerPage = 10,
  currentPage = 1,
  totalPages = Math.ceil(totalItem / itemPerPage) || 1,
  onPageChange,
  fetchItems,
  filterParams = {},
}) => {
  const internalPage = currentPage - 1;
  const isMobile = useMediaQuery("(max-width:768px)");

  const handlePrevClick = async () => {
    if (internalPage > 0) {
      const newPage = internalPage - 1;
      onPageChange(newPage + 1);
      await fetchItems(newPage + 1, filterParams);
    }
  };

  const handleNextClick = async () => {
    if (internalPage < totalPages - 1) {
      const newPage = internalPage + 1;
      onPageChange(newPage + 1);
      await fetchItems(newPage + 1, filterParams);
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = isMobile ? 3 : 5;
    let startPage, endPage;

    if (totalPages <= maxVisiblePages) {
      startPage = 0;
      endPage = totalPages - 1;
    } else if (internalPage <= Math.floor(maxVisiblePages / 2)) {
      startPage = 0;
      endPage = maxVisiblePages - 1;
    } else if (internalPage + Math.floor(maxVisiblePages / 2) >= totalPages) {
      startPage = totalPages - maxVisiblePages;
      endPage = totalPages - 1;
    } else {
      startPage = internalPage - Math.floor(maxVisiblePages / 2);
      endPage = internalPage + Math.floor(maxVisiblePages / 2);
    }

    if (startPage > 0) {
      pageNumbers.push(
        <button
          key="0"
          onClick={() => {
            onPageChange(1);
            fetchItems(1, filterParams);
          }}
          style={{
            cursor: "pointer",
            background: "none",
            border: "none",
            padding: "0 8px",
            fontSize: isMobile ? "12px" : "16px",
            flexShrink: 0,
          }}
        >
          1
        </button>
      );
      if (startPage > 1)
        pageNumbers.push(
          <span key="ellipsis-left" style={{ padding: "0 4px" }}>
            ...
          </span>
        );
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => {
            onPageChange(i + 1);
            fetchItems(i + 1, filterParams);
          }}
          style={{
            backgroundColor: internalPage === i ? "#2E4053" : "#A6AAAD",
            color: "#FFFFFF",
            padding: isMobile ? "4px 8px" : "8px 16px",
            borderRadius: "4px",
            margin: "0 4px",
            fontSize: isMobile ? "12px" : "16px",
            border: "none",
            flexShrink: 0, // Prevents button from getting thin
            cursor: "pointer",
          }}
        >
          {i + 1}
        </button>
      );
    }

    if (endPage < totalPages - 1) {
      if (endPage < totalPages - 2) {
        pageNumbers.push(
          <span key="ellipsis-right" style={{ padding: "0 4px" }}>
            ...
          </span>
        );
      }
      pageNumbers.push(
        <button
          key={totalPages - 1}
          onClick={() => {
            onPageChange(totalPages);
            fetchItems(totalPages, filterParams);
          }}
          style={{
            cursor: "pointer",
            background: "none",
            border: "none",
            padding: "0 8px",
            fontSize: isMobile ? "12px" : "16px",
            flexShrink: 0,
          }}
        >
          {totalPages}
        </button>
      );
    }

    return pageNumbers;
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap", // Forces same line
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        position: "fixed",
        bottom: 0,
        left: 0,

        padding: "15px 0",
        zIndex: 1000,
      }}
    >
      {/* Prev Button */}
      <button
        onClick={handlePrevClick}
        disabled={internalPage === 0}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          width: isMobile ? "30px" : "45px",
          height: isMobile ? "30px" : "45px",
          marginRight: isMobile ? "10px" : "20px",
          color: "#FFFFFF",
          backgroundColor: internalPage === 0 ? "#D5DBDB" : "#A6AAAD",
          border: "none",
          flexShrink: 0, // IMPORTANT
          cursor: internalPage === 0 ? "not-allowed" : "pointer",
        }}
      >
        &larr;
      </button>

      {/* Page Numbers Container */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          flexWrap: "nowrap", // IMPORTANT
        }}
      >
        {totalPages > 1 ? renderPageNumbers() : null}
      </div>

      {/* Next Button */}
      <button
        onClick={handleNextClick}
        disabled={internalPage === totalPages - 1}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          width: isMobile ? "30px" : "45px",
          height: isMobile ? "30px" : "45px",
          marginLeft: isMobile ? "10px" : "20px",
          color: "#FFFFFF",
          backgroundColor:
            internalPage === totalPages - 1 ? "#D5DBDB" : "#A6AAAD",
          border: "none",
          flexShrink: 0, // IMPORTANT
          cursor: internalPage === totalPages - 1 ? "not-allowed" : "pointer",
        }}
      >
        &rarr;
      </button>
    </div>
  );
};

Pagination.propTypes = {
  totalItem: PropTypes.number.isRequired,
  itemPerPage: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  fetchItems: PropTypes.func.isRequired,
  filterParams: PropTypes.object,
};

export default Pagination;
